import type { AllAlert } from '~/@types/redux';
import { RULE_RESULT_STATUS_ID } from '~/common/constants';
import {
  ALERTS_TO_SKIP_IF_CLEARED,
  CATEGORY_IDS,
} from '~/pages/Home/constants';

/**
 * Shared predicate — an alert requires attention when it is FIRED,
 * or CLEARED for an underwriter (who must review cleared items too).
 */
const isValidAlert = (alert: AllAlert, isUnderwriter: boolean): boolean =>
  alert?.ruleResultStatusId === RULE_RESULT_STATUS_ID.FIRED ||
  (isUnderwriter &&
    alert?.ruleResultStatusId === RULE_RESULT_STATUS_ID.CLEARED);

/**
 * Discriminated union returned by resolveNextNavigation.
 * Consumers switch on `action` to determine what to dispatch.
 */
export type NextNavigation =
  | { action: 'CLASSIFY' }
  | { action: 'NAVIGATE'; alert: AllAlert }
  | { action: 'LAST_ALERT' };

export const getVisibleCategoryId = (
  alert: AllAlert | null | undefined,
): number => alert?.navigationCategoryId ?? alert?.ruleCategoryId;

export const getNextClassifyAlert = (
  allAlerts: AllAlert[],
  selectedAlert: AllAlert | null,
): AllAlert | undefined => {
  const currentIndex = allAlerts.findIndex(
    (alert) =>
      (selectedAlert?.ruleReferenceId != null &&
        alert.ruleReferenceId === selectedAlert.ruleReferenceId) ||
      (selectedAlert?.gutterItemId != null &&
        alert.gutterItemId === selectedAlert.gutterItemId),
  );

  if (currentIndex < 0) {
    return undefined;
  }

  // stay in the action needed or completed group while moving forward
  const alertsAfterCurrent = allAlerts.slice(currentIndex + 1);
  const nextInStatusGroup = alertsAfterCurrent.find(
    (alert) =>
      getVisibleCategoryId(alert) === CATEGORY_IDS.CLASSIFY &&
      alert.ruleResultStatusId === selectedAlert?.ruleResultStatusId,
  );

  if (nextInStatusGroup) {
    return nextInStatusGroup;
  }

  // once there are no more FIRED alerts, continue into the completed group
  if (selectedAlert?.ruleResultStatusId === RULE_RESULT_STATUS_ID.FIRED) {
    return alertsAfterCurrent.find(
      (alert) =>
        getVisibleCategoryId(alert) === CATEGORY_IDS.CLASSIFY &&
        alert.ruleResultStatusId === RULE_RESULT_STATUS_ID.CLEARED,
    );
  }

  return undefined;
};

/**
 * Underwriter path — advances linearly through allAlerts from the current
 * position without skipping alerts based on their status or top-nav icon.
 * Classify is not a waypoint on this path; the sequence ends with LAST_ALERT.
 */
const resolveNextNavigationForUnderwriter = (
  allAlerts: AllAlert[],
  selectedAlert: AllAlert | null,
): NextNavigation => {
  const currentIndex = selectedAlert
    ? allAlerts.findIndex((a) => a.gutterItemId === selectedAlert.gutterItemId)
    : -1;

  const nextAlert = allAlerts[currentIndex + 1] ?? null;

  return nextAlert
    ? { action: 'NAVIGATE', alert: nextAlert }
    : { action: 'LAST_ALERT' };
};

/**
 * Default (non-underwriter) path — category-first, then Classify, then top of list.
 *
 * Navigation order:
 *  1. Same category — if any valid FIRED alerts remain in the current ruleCategoryId, go there next.
 *  2. Classify — once the category is exhausted, Classify takes priority if it has pending work.
 *  3. Top of list — navigate to the first valid alert in allAlerts.
 *  4. LAST_ALERT — emitted when no valid alerts remain anywhere.
 */
const resolveNextNavigationDefault = (
  allAlerts: AllAlert[],
  selectedAlert: AllAlert | null,
): NextNavigation => {
  const currentCategoryId = selectedAlert?.ruleCategoryId;
  const currentGutterItemId = selectedAlert?.gutterItemId;

  const isOtherValidAlert = (alert: AllAlert): boolean =>
    isValidAlert(alert, false) && alert.gutterItemId !== currentGutterItemId;

  // Stay in the current category until all its items are complete.
  const nextInCategory =
    allAlerts?.find(
      (alert) =>
        isOtherValidAlert(alert) && alert.ruleCategoryId === currentCategoryId,
    ) ?? null;

  if (nextInCategory) {
    return { action: 'NAVIGATE', alert: nextInCategory };
  }

  // Current category exhausted — Classify takes priority before moving to top of list.
  const classifyActionRequired =
    selectedAlert?.ruleCategoryId !== CATEGORY_IDS.CLASSIFY &&
    allAlerts.find(
      (x: AllAlert) =>
        x.ruleCategoryId === CATEGORY_IDS.CLASSIFY &&
        x.ruleResultStatusId === RULE_RESULT_STATUS_ID.FIRED,
    );

  if (classifyActionRequired) {
    return {
      action: 'NAVIGATE',
      alert: classifyActionRequired,
    };
  }

  // Go to the top-most valid alert across all categories.
  const nextTop = allAlerts?.find(isOtherValidAlert) ?? null;

  return nextTop
    ? { action: 'NAVIGATE', alert: nextTop }
    : { action: 'LAST_ALERT' };
};

/**
 * Pure function that resolves the next navigation destination after the user
 * completes an alert. Designed to be framework-agnostic so it can be consumed
 * by Redux thunks today and TanStack Query mutations in the future.
 *
 * The QUICK_MATCH early-exit is intentionally left to the caller since it is a
 * Redux-specific concern (setting selectedAlert to null) rather than navigation.
 *
 * Underwriters navigate to each alert in allAlerts array order without
 * skipping alerts, including a green-shield tab. Non-underwriters use the
 * category-first strategy.
 *
 * @param allAlerts   Full alerts list sorted by parentRuleSortOrder.
 * @param selectedAlert The currently active alert (null if none).
 * @param isUnderwriter Whether the current user is an underwriter.
 */
export const resolveNextNavigation = (
  allAlerts: AllAlert[],
  selectedAlert: AllAlert | null,
  isUnderwriter: boolean,
): NextNavigation => {
  if (isUnderwriter) {
    return resolveNextNavigationForUnderwriter(allAlerts, selectedAlert);
  }

  return resolveNextNavigationDefault(allAlerts, selectedAlert);
};

// get & set the first item in the alerts as selectedAlert on pageLoad
// skip certain cleared alerts (Income Summary for now, this list should **NOT** grow)
export const getNextCategoryAlert = (alerts) => {
  let nextAlert;
  const nextValidAlert = (alert) =>
    !(
      alert.ruleResultStatusId === RULE_RESULT_STATUS_ID.CLEARED &&
      ALERTS_TO_SKIP_IF_CLEARED.includes(alert.ruleName)
    );
  if (alerts[0]?.gutterItemChildren?.length) {
    nextAlert = alerts[0]?.gutterItemChildren.find(nextValidAlert);
  } else {
    nextAlert = alerts.find(nextValidAlert);
  }
  return nextAlert;
};
